import { PDFDocument, PDFHexString, PDFName, PDFString, degrees, BlendMode } from 'pdf-lib';
import { EXPORT_SCALE, OBJ } from '../constants';
import {
    ensureEditorFonts,
    hasHighlights,
    hasNonHighlights,
    objectsBounds,
    paintObjects,
    preloadObjectImages,
} from './drawObjects';
import { applyImageSwaps } from './imageSwap';
import { stripReplacedText } from './stripReplaced';
import {
    createDocumentFontCache,
    createFontCache,
    drawVectorText,
    hexToRgb,
    resolveRunFonts,
} from './vectorText';

/**
 * Turn the editor state back into a PDF.
 *
 * Original pages are *copied*, never rasterised, so text stays selectable and
 * the file stays small. Annotations are painted onto a transparent canvas —
 * only over the region they occupy — and stamped on top as a PNG. Highlights
 * get their own stamp with a PDF multiply blend so the text underneath still
 * reads. Links are emitted as real PDF link annotations.
 */

/**
 * Map a view-space rect onto the page's user space.
 *
 * `rotation` is the page's *original* `/Rotate`, which is the rotation pdf.js
 * baked into the view the objects were drawn in. Any extra rotation the user
 * added is applied to the whole page afterwards, so the stamp turns with the
 * content instead of drifting off it.
 */
function placeStamp(rect, rotation, box) {
    const { x: cx, y: cy, width: W, height: H } = box;
    const { x: bx, y: by, w: bw, h: bh } = rect;

    switch (rotation) {
        case 90:
            return { x: cx + by + bh, y: cy + bx, width: bw, height: bh, rotate: degrees(90) };
        case 180:
            return { x: cx + W - bx, y: cy + by + bh, width: bw, height: bh, rotate: degrees(180) };
        case 270:
            return { x: cx + W - (by + bh), y: cy + H - bx, width: bw, height: bh, rotate: degrees(270) };
        default:
            return { x: cx + bx, y: cy + H - (by + bh), width: bw, height: bh, rotate: degrees(0) };
    }
}

/** View-space point → user-space point (same convention as `placeStamp`). */
function mapPoint(u, v, rotation, box) {
    const { x: cx, y: cy, width: W, height: H } = box;
    switch (rotation) {
        case 90: return [cx + v, cy + u];
        case 180: return [cx + W - u, cy + v];
        case 270: return [cx + W - v, cy + H - u];
        default: return [cx + u, cy + H - v];
    }
}

function renderStampCanvas(objects, bounds, layer, vectorPatchIds) {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bounds.w * EXPORT_SCALE));
    canvas.height = Math.max(1, Math.round(bounds.h * EXPORT_SCALE));
    const ctx = canvas.getContext('2d');
    ctx.translate(-bounds.x * EXPORT_SCALE, -bounds.y * EXPORT_SCALE);
    paintObjects(ctx, objects, EXPORT_SCALE, { layer, forExport: true, vectorPatchIds });
    return canvas;
}

const overlaps = (a, b) => a.x < b.x + b.w && b.x < a.x + a.w
    && a.y < b.y + b.h && b.y < a.y + a.h;

/**
 * Text patches that can be drawn as real PDF rectangles instead of being baked
 * into the stamp.
 *
 * Baked in, the patch ends where the image's alpha falls to zero, and a viewer
 * that interpolates the image (Chrome's does) blends that edge with transparent
 * black — the faint grey rectangle that shows around an edited paragraph in the
 * download but never in the preview.
 *
 * A vector rect is painted *under* the whole stamp, so a box is only eligible
 * while nothing stamped below it overlaps: otherwise the patch would slide
 * behind an annotation it is meant to cover.
 */
const patchColour = (obj) => {
    if (obj.type === OBJ.WHITEOUT) return obj.fill;
    if (obj.type === OBJ.TEXT) return obj.background;
    return null;
};

function vectorPatchSet(stamped) {
    const ids = new Set();
    stamped.forEach((obj, i) => {
        const colour = patchColour(obj);
        if (!colour || colour === 'transparent' || obj.rotation) return;
        if (stamped.slice(0, i).some((below) => overlaps(obj, below))) return;
        ids.add(obj.id);
    });
    return ids;
}

function drawVectorPatches(page, objects, ids, rotation, box) {
    for (const obj of objects) {
        if (!ids.has(obj.id)) continue;
        const corners = [
            mapPoint(obj.x, obj.y, rotation, box),
            mapPoint(obj.x + obj.w, obj.y, rotation, box),
            mapPoint(obj.x + obj.w, obj.y + obj.h, rotation, box),
            mapPoint(obj.x, obj.y + obj.h, rotation, box),
        ];
        const xs = corners.map((c) => c[0]);
        const ys = corners.map((c) => c[1]);
        page.drawRectangle({
            x: Math.min(...xs),
            y: Math.min(...ys),
            width: Math.max(...xs) - Math.min(...xs),
            height: Math.max(...ys) - Math.min(...ys),
            color: hexToRgb(patchColour(obj)),
        });
    }
}

async function canvasToPngBytes(canvas) {
    const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('stampFailed'))), 'image/png');
    });
    return new Uint8Array(await blob.arrayBuffer());
}

/**
 * The document outline — the panel a reader opens down the side of a PDF.
 *
 * pdf-lib has no outline API, so the tree is written by hand: one dictionary
 * per entry, linked to its parent, its two neighbours and its children, with
 * the catalogue pointed at the root. Titles go in as hex strings, which is the
 * only way a Bangla heading survives — a literal string is Latin-1.
 */
function writeOutlines(outDoc, bookmarks, pageRefs) {
    const items = (bookmarks || []).filter((b) => b?.title?.trim() && pageRefs[b.pageIndex]);
    if (!items.length) return;

    const { context, catalog } = outDoc;
    const rootRef = context.nextRef();
    // Every entry needs a ref before any dictionary is built: siblings point at
    // each other, so the second one has to exist while the first is written.
    const refs = items.map(() => context.nextRef());

    // A child belongs to the nearest entry above it that sits a level higher.
    const parentOf = items.map((item, i) => {
        if (!item.level) return null;
        for (let j = i - 1; j >= 0; j -= 1) if ((items[j].level || 0) < item.level) return j;
        return null;
    });

    const childrenOf = new Map();
    items.forEach((_, i) => {
        const key = parentOf[i];
        if (!childrenOf.has(key)) childrenOf.set(key, []);
        childrenOf.get(key).push(i);
    });

    const descendants = (i) => (childrenOf.get(i) || [])
        .reduce((total, child) => total + 1 + descendants(child), 0);

    items.forEach((item, i) => {
        const kids = childrenOf.get(i) || [];
        const siblings = childrenOf.get(parentOf[i]) || [];
        const at = siblings.indexOf(i);
        const dict = {
            Title: PDFHexString.fromText(item.title.trim()),
            Parent: parentOf[i] === null ? rootRef : refs[parentOf[i]],
            Dest: [pageRefs[item.pageIndex], PDFName.of('Fit')],
        };
        if (at > 0) dict.Prev = refs[siblings[at - 1]];
        if (at < siblings.length - 1) dict.Next = refs[siblings[at + 1]];
        if (kids.length) {
            dict.First = refs[kids[0]];
            dict.Last = refs[kids[kids.length - 1]];
            // Positive opens the branch; negative would show it collapsed.
            dict.Count = descendants(i);
        }
        context.assign(refs[i], context.obj(dict));
    });

    const top = childrenOf.get(null) || [];
    if (!top.length) return;
    context.assign(rootRef, context.obj({
        Type: 'Outlines',
        First: refs[top[0]],
        Last: refs[top[top.length - 1]],
        Count: items.length,
    }));
    catalog.set(PDFName.of('Outlines'), rootRef);
    // Open the reader on the outline, which is the point of having one.
    catalog.set(PDFName.of('PageMode'), PDFName.of('UseOutlines'));
}

function addLinkAnnotations(outDoc, page, links, rotation, box, pageRefs) {
    if (!links.length) return;
    const { context } = outDoc;

    for (const link of links) {
        const corners = [
            mapPoint(link.x, link.y, rotation, box),
            mapPoint(link.x + link.w, link.y, rotation, box),
            mapPoint(link.x + link.w, link.y + link.h, rotation, box),
            mapPoint(link.x, link.y + link.h, rotation, box),
        ];
        const xs = corners.map((c) => c[0]);
        const ys = corners.map((c) => c[1]);
        const rect = [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];

        let action = null;
        if (link.url) {
            const href = /^(https?:|mailto:|tel:)/i.test(link.url) ? link.url : `https://${link.url}`;
            action = context.obj({ Type: 'Action', S: 'URI', URI: PDFString.of(href) });
        } else if (Number.isInteger(link.targetPage) && pageRefs[link.targetPage]) {
            action = context.obj({
                Type: 'Action',
                S: 'GoTo',
                D: [pageRefs[link.targetPage], PDFName.of('Fit')],
            });
        }
        if (!action) continue;

        const annot = context.obj({
            Type: 'Annot',
            Subtype: 'Link',
            Rect: rect,
            Border: [0, 0, 0],
            F: 4,
            A: action,
        });
        page.node.addAnnot(context.register(annot));
    }
}

/**
 * @param {ArrayBuffer} sourceBytes original PDF bytes
 * @param {Array} pages editor page list (order already applied)
 * @param {Record<string, Array>} objectsByUid annotations per page uid
 * @param {{ onProgress?: (done: number, total: number) => void, pdf?: object }} [options]
 *   `pdf` is the pdf.js document the editor is showing. It is what makes an
 *   edited paragraph exportable as real text: its font programs are re-embedded
 *   instead of the paragraph being stamped as an image.
 * @returns {Promise<Blob>}
 */
export async function buildEditedPdf(sourceBytes, pages, objectsByUid, options = {}) {
    const {
        onProgress, pdf, bookmarks = [], language = null, title = null,
    } = options;

    let src;
    try {
        src = await PDFDocument.load(sourceBytes.slice(0), { ignoreEncryption: true });
    } catch (err) {
        if (String(err?.message || '').toLowerCase().includes('encrypt')) throw new Error('password');
        throw err;
    }

    const out = await PDFDocument.create();
    out.setProducer('Sohozkaj PDF Editor');
    out.setCreator('Sohozkaj');
    // Two things a screen reader asks the file for before it reads a word: what
    // the document is called, and what language it is in. Neither costs
    // anything to write, and the accessibility check asks for both.
    if (title?.trim()) out.setTitle(title.trim());
    if (language) out.catalog.set(PDFName.of('Lang'), PDFString.of(language === 'bn' ? 'bn-BD' : 'en'));

    const allObjects = pages.flatMap((p) => objectsByUid[p.uid] || []);
    await preloadObjectImages(allObjects);
    // Stamps are drawn by the browser canvas, so every family the document uses
    // has to be loaded here too — an unfetched one would stamp in a fallback
    // face even though the preview showed the real thing.
    await ensureEditorFonts(allObjects);

    // One copyPages call for every source page (duplicates included) lets
    // pdf-lib share resources between them instead of re-embedding fonts.
    const sourceIndices = pages.filter((p) => p.sourceIndex != null).map((p) => p.sourceIndex);
    const copied = sourceIndices.length ? await out.copyPages(src, sourceIndices) : [];

    let cursor = 0;
    const placed = pages.map((p) => {
        if (p.sourceIndex == null) {
            const { width, height } = blankSize(p);
            return out.addPage([width, height]);
        }
        const page = copied[cursor];
        cursor += 1;
        out.addPage(page);

        return page;
    });

    /**
     * Put a page on different paper: its own content is scaled and re-centred
     * on the new size, so an A4 contract printed on Letter is the same
     * contract rather than a cropped one.
     *
     * Embedding the original as a form and drawing it instead looked simpler
     * and was wrong twice over — the paragraph "Edit text" had replaced could
     * no longer be cut out of the content stream, so the old words came back,
     * and everything this editor adds landed in the old coordinate space.
     *
     * Called from inside the page loop, *after* the source text is stripped and
     * *before* anything is drawn. Both halves matter: the strip works on the
     * page's own operators, which are still in the original space, while
     * everything this editor adds was laid out against the new page size.
     */
    /**
     * Everything this editor adds, in the source page's coordinates.
     *
     * On resized paper the store has already moved every object on to the new
     * sheet, and the page's own content is scaled on to it at the very end of
     * the block below. Drawing has to happen in the space the content is still
     * in when it happens — so the objects come back off the sheet first, and
     * the same transform carries them and the page's own ink over together.
     *
     * `replaces` is left alone: it always described the source page.
     */
    const toSourceSpace = (objects, p) => {
        const k = p.contentScale;
        if (!k || k === 1) return objects;
        const dx = p.contentOffset?.x || 0;
        const dy = p.contentOffset?.y || 0;
        return objects.map((obj) => ({
            ...obj,
            x: (obj.x - dx) / k,
            y: (obj.y - dy) / k,
            w: obj.w / k,
            h: obj.h / k,
            ...(obj.fontSize ? { fontSize: obj.fontSize / k } : null),
            ...(obj.strokeWidth ? { strokeWidth: obj.strokeWidth / k } : null),
        }));
    };

    /**
     * Put the page on its new paper — last, once everything is drawn.
     *
     * Order is the whole trick. pdf-lib wraps the page's *entire* content in
     * the scaling matrix, and anything already drawn goes inside that wrap
     * with it; anything drawn afterwards may or may not, depending on which
     * content stream pdf-lib happens to append to. Scaling at the end makes
     * the question moot: the page's own ink, this editor's stamps and its
     * annotations are all carried over by one transform.
     */
    const applyResize = (page, p) => {
        if (!p.resized) return;
        const box = page.getCropBox();
        // The store works the transform out when the paper is changed and moves
        // everything on the page by it, so the export has to use the same
        // numbers rather than derive its own — two resizes in a row do not
        // compose into one fit, and the objects would then sit a few points off
        // the content they were placed against.
        const scale = p.contentScale || Math.min(p.width / box.width, p.height / box.height);
        // Fit, never fill: centre whatever direction has room left over.
        const dx = p.contentOffset?.x ?? (p.width - box.width * scale) / 2;
        const dy = p.contentOffset?.y ?? (p.height - box.height * scale) / 2;
        page.setSize(p.width, p.height);
        page.scaleContent(scale, scale);
        page.scaleAnnotations(scale, scale);
        // PDF space counts up from the bottom; the offset was measured from the
        // top, which is where the objects' own y comes from.
        page.translateContent(dx, p.height - box.height * scale - dy);
    };

    const pageRefs = placed.map((page) => page.ref);
    const embedFont = createFontCache(out);
    const embedDocumentFont = createDocumentFontCache(out, pdf);

    for (let i = 0; i < pages.length; i += 1) {
        const meta = pages[i];
        const page = placed[i];
        const baseRotation = ((meta.baseRotation || 0) % 360 + 360) % 360;
        const finalRotation = ((baseRotation + (meta.userRotation || 0)) % 360 + 360) % 360;
        page.setRotation(degrees(finalRotation));

        let objects = objectsByUid[meta.uid] || [];
        if (!objects.length) {
            applyResize(page, meta);
        } else {
            // Delete the paragraphs "Edit text" replaced, rather than only
            // covering them. Without this the old words stay in the file under
            // the patch — selectable, searchable and copied out alongside the
            // new ones — which is the difference between editing a PDF and
            // pasting over it.
            //
            // Shared with the canvas, which strips a throwaway copy of the
            // document so it can draw the page without the old text instead of
            // hiding it. One implementation, so the two cannot disagree about
            // which patches are still needed.
            // Pictures are swapped in the page's own resources — before the
            // resize, because the rect that identifies one was measured on the
            // source page. The object itself draws nothing.
            await applyImageSwaps(out, page, meta, objects);
            objects = objects.filter((o) => o.type !== OBJ.IMAGE_SWAP);

            const stripped = stripReplacedText(page, meta, objects);
            // The patch only exists because the canvas cannot show a page with
            // the text taken out. Where the text really is gone, painting it
            // would cover whatever sat behind the paragraph for nothing.
            if (stripped.size) {
                objects = objects.filter((o) => !(o.patchFor && stripped.has(o.patchFor)));
            }

            // Everything below is drawn against the page as it still is; the
            // new paper is applied once, at the end.
            objects = toSourceSpace(objects, meta);
            const cropBox = page.getCropBox();
            const box = {
                x: cropBox.x,
                y: cropBox.y,
                width: cropBox.width,
                height: cropBox.height,
            };
            const viewW = baseRotation % 180 === 90 ? box.height : box.width;
            const viewH = baseRotation % 180 === 90 ? box.width : box.height;

            // Text is written as real PDF text wherever a font can be had for
            // it — crisp, selectable and small. A raster stamp of the same words
            // reads as a soft halo next to the untouched vector text around it,
            // and its alpha edge draws a faint rectangle in viewers that
            // interpolate images.
            //
            // Two sources: a standard font for the Latin catalogue families, or
            // the document's own font program re-embedded (that is what an
            // "Edit text" box is set in). The embedded one is only used if it
            // measures like the canvas did — a subset can be missing a glyph
            // the user typed, which would export as a blank.
            const runFonts = new Map();
            for (const obj of objects) {
                if (obj.type !== OBJ.TEXT) continue;
                // eslint-disable-next-line no-await-in-loop
                const fonts = await resolveRunFonts(obj, { embedFont, embedDocumentFont });
                if (fonts) runFonts.set(obj.id, fonts);
            }

            const vector = objects.filter((o) => runFonts.has(o.id));
            const stamped = objects.filter((o) => !vector.includes(o));

            // Links are painted too (their underline is the only visible cue)
            // *and* emitted as real annotations below.
            const patchIds = vectorPatchSet(stamped);
            drawVectorPatches(page, stamped, patchIds, baseRotation, box);
            // A whiteout that became a rectangle has nothing left to paint; a
            // text box still has its glyphs. Dropping the empty ones keeps a
            // page whose only annotation was a patch free of an all-transparent
            // stamp image.
            const toStamp = stamped.filter((o) => !(patchIds.has(o.id) && o.type === OBJ.WHITEOUT));
            if (hasNonHighlights(toStamp)) {
                // eslint-disable-next-line no-await-in-loop
                await stampLayer(out, page, toStamp, 'normal', viewW, viewH, baseRotation, box, patchIds);
            }
            if (hasHighlights(stamped)) {
                // eslint-disable-next-line no-await-in-loop
                await stampLayer(out, page, stamped, 'highlight', viewW, viewH, baseRotation, box);
            }

            for (const obj of vector) {
                // eslint-disable-next-line no-await-in-loop
                await drawVectorText(page, obj, {
                    fonts: runFonts.get(obj.id),
                    rotation: baseRotation,
                    place: (u, v) => mapPoint(u, v, baseRotation, box),
                });
            }

            addLinkAnnotations(
                out,
                page,
                objects.filter((o) => o.type === OBJ.LINK && (o.url || Number.isInteger(o.targetPage))),
                baseRotation,
                box,
                pageRefs,
            );

            applyResize(page, meta);
        }

        onProgress?.(i + 1, pages.length);
    }

    writeOutlines(out, bookmarks, pageRefs);

    const bytes = await out.save({ useObjectStreams: true });
    return new Blob([bytes], { type: 'application/pdf' });
}

async function stampLayer(outDoc, page, objects, layer, viewW, viewH, rotation, box, vectorPatchIds) {
    const subset = objects.filter((o) => (layer === 'highlight'
        ? o.type === OBJ.INK && o.mode === 'highlight'
        : !(o.type === OBJ.INK && o.mode === 'highlight')));
    if (!subset.length) return;

    const bounds = objectsBounds(subset, viewW, viewH);
    if (!bounds || bounds.w < 0.5 || bounds.h < 0.5) return;

    const canvas = renderStampCanvas(subset, bounds, layer, vectorPatchIds);
    const png = await outDoc.embedPng(await canvasToPngBytes(canvas));
    const placement = placeStamp(bounds, rotation, box);
    page.drawImage(png, {
        ...placement,
        ...(layer === 'highlight' ? { blendMode: BlendMode.Multiply } : {}),
    });
}

function blankSize(page) {
    // Blank pages are stored in view space with no rotation, so the two agree.
    return { width: page.width, height: page.height };
}

/** Open a blob in a hidden iframe and trigger the browser print dialog. */
export function printPdfBlob(blob) {
    const url = URL.createObjectURL(blob);
    const frame = document.createElement('iframe');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    frame.src = url;
    frame.onload = () => {
        try {
            frame.contentWindow.focus();
            frame.contentWindow.print();
        } catch {
            window.open(url, '_blank', 'noopener');
        }
        // Keep the frame alive long enough for the print dialog to read it.
        setTimeout(() => {
            frame.remove();
            URL.revokeObjectURL(url);
        }, 60000);
    };
    document.body.appendChild(frame);
}
